// The Vue build version to load with the `import` command
// (runtime-only or standalone) has been set in webpack.base.conf with an alias.
import { createApp, h } from 'vue';
import moment from 'moment';
import * as CSDesignSystem from '@complispace/cs-design-system';
import { axios, i18n, translator, vuetifyBuilder } from '@complispace/cs-design-system';
import VueGtag from 'vue-gtag';
import { mapState } from 'vuex';
import * as types from '@/store/mutationTypes';
import { iconAliases } from './resources/assets/icons';
import SlotIndicator from './components/core/SlotIndicator';
import 'vuetify/styles';
import 'material-design-icons-iconfont/dist/material-design-icons.css';
import '@mdi/font/css/materialdesignicons.css';
import '@fortawesome/fontawesome-free/css/all.css';
import './main.css';
import '@complispace/cs-design-system/style.css';
import '@ideagen-hlx/helix-vue/dist/style.css';

import App from './App';
import router, { beforeEachRoute } from './router';
import store from './store';

import { plugIns } from './lib/globalPlugIns';
import { setupAxiosInterceptors } from './lib/axiosInterceptors';
import { bootstrapSupportSite } from './lib/supportSiteBootstrap';

/* If we need to supress Vue warnings for dynamic content changes
Vue.config.warnHandler = function customWarnHandler(err, vm, info) {
    // handle error
    // `info` is a Vue-specific error info, e.g. which lifecycle hook
    // the error was found in. Only available in 2.2.0+
    console.log('Custom vue warn handler: ', err, vm.name, info);
};
*/

const vuetify = vuetifyBuilder({ themeName: 'ce' }).build();

const options = {
  store,
  router,
  beforeEachRoute,
  hub: {
    appEnv: process.env.APP_ENV,
    appHost: window.location.host,
    appUrl: window.location.origin,
    appName: 'PolicyConnect'
  },
  auth0: {
    appHost: window.location.host,
    appUrl: window.location.origin,
    auth0Domain: process.env.AUTH0_DOMAIN,
    auth0ClientId: process.env.AUTH0_CLIENT_ID,
    auth0Audience: process.env.AUTH0_AUDIENCE,
    appEnv: process.env.APP_ENV
  },
  login: {
    resetBtnTimeout: parseInt(process.env.VUE_APP_LOGIN_RESET_TIMEOUT || 5, 10)
  },
  // Only include gainsight config if the required values are present
  ...(process.env.GAINSIGHT_SITE_INFO_API_HOST && {
    gainsight: {
      gainsightSiteInfoApiHost: process.env.GAINSIGHT_SITE_INFO_API_HOST,
      gainsightProductTag: process.env.GAINSIGHT_PRODUCT_TAG,
      gainsightAppName: process.env.GAINSIGHT_APP_NAME
    }
  })
};

const app = createApp({
  i18n,
  computed: {
    ...mapState({
      refreshToken: (state) => state.session.refreshToken,
      idpUri: (state) => state.session.idpUri
    })
  },
  mounted() {
    this.$el.addEventListener('click', this.onClickVue);
  },
  beforeUnmount() {
    this.$el.removeEventListener('click', this.onClickVue);
  },
  methods: {
    popupLogin() {
      if (this.refreshToken) {
        this.$store.commit(types.SESSION_SET_REFRESH_TOKEN, false);

        const refreshUri = `${window.location.origin}/static/refresh.html`;
        const queryParams = `client_id=quicklinks&redirect_uri=${encodeURIComponent(
          refreshUri
        )}&scope=blah&response_type=token&state=`;
        const url = `https://${this.idpUri}/oauth2/authorize?${queryParams}`;

        const height = 500;
        const width = 400;
        const ow = window.open(
          url,
          //          const ow = window.open("http://testschool.localhost:9083/static/test.html#launch",
          'Refresh Session',
          // eslint-disable-next-line no-restricted-globals
          `height=${height},width=${width},left=${(screen.width - width) / 2},top=${
            // eslint-disable-next-line no-restricted-globals
            (screen.height - height) / 2
          },resizable=yes,scrollbars=yes,toolbar=yes,menubar=no,location=no,directories=no, status=yes`
        );

        const that = this;

        window.addEventListener('message', function elcb(event) {
          // Do we trust the sender of this message?
          if (event.source !== ow) return;
          if (event.origin !== window.location.origin && !event.data.includes('access_token'))
            return;

          that.$store.dispatch('session/refresh', {
            uri: event.data
          });
          // FYI, hotjar and other things send 'message' so
          // must be sure we get the right one before removing
          window.removeEventListener('message', elcb);
          ow.close();
        });
      }
    },
    onClickVue() {
      this.popupLogin();
    }
  },
  render: () => h(App)
});

// Base Vue config
app.config.performance = true;
// eslint-disable-next-line no-multi-assign
app.config.globalProperties.$http = axios;
app.config.globalProperties.$app = { plugIns };

app.component('SlotIndicator', SlotIndicator);

// Internationalization

// applyAppI18n expects { en: {...}, zh: {...} } — bare locale keys mapping to flat
// message objects. import.meta.glob yields module namespaces keyed by full path, so
// both the key and the value need normalising.
const reqRes = import.meta.glob('./resources/i18n/*.json', { eager: true });
const i18nModules = {};
Object.entries(reqRes).forEach(([key, mod]) => {
  const cleanKey = key.replace(/^.*\//, '').replace(/\.json$/, '');
  i18nModules[cleanKey] = mod.default ?? mod;
});
translator.applyAppI18n(i18nModules);

app.use(vuetify);

// Must finish before CSDS and the store make any API call so the target is validated first.
// Not top-level await: build target is es2020. A bootstrap failure must not stop the app starting.
bootstrapSupportSite(store)
  .catch((err) => {
    console.error('Support site bootstrap failed:', err);
  })
  .then(() => {
    app.use(CSDesignSystem, options);

    app.use(store);
    app.use(router);

    app.config.globalProperties.$dateformat = (dataStr, pattern = 'YYYY-MM-DD HH:mm:ss') => {
      return moment(dataStr).format(pattern);
    };

    if (process.env.APP_ENV !== 'automation') {
      app.use(
        VueGtag,
        {
          config: {
            id: process.env.GOOGLE_ANALYTICS_ID,
            params: {
              send_page_view: false
            }
          }
        },
        router
      );
    }

    setupAxiosInterceptors(store, router);

    app.mount('#app');
  });

// Add custom icons to vuetify dynamically
vuetify.icons.aliases = {
  ...vuetify.icons.aliases,
  ...iconAliases
};
